import React from 'react';
import { useNode } from '@craftjs/core';
import { AUTO_FIT_ROW, autoFitItem, normalizeColumns } from '../../utils/autoFitRow';
import {
  ShieldCheck,
  BadgeCheck,
  Award,
  Search,
  RefreshCcw,
  Banknote,
  Clock,
  Wrench,
  FileCheck,
  Headphones,
  Truck,
  Lock,
  Sparkles,
  Handshake,
  ThumbsUp,
} from 'lucide-react';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';

type TrustIconName =
  | 'shield-check'
  | 'badge-check'
  | 'award'
  | 'search'
  | 'refresh-ccw'
  | 'banknote'
  | 'clock'
  | 'wrench'
  | 'file-check'
  | 'headphones'
  | 'truck'
  | 'lock'
  | 'sparkles'
  | 'handshake'
  | 'thumbs-up';

const iconMap: Record<TrustIconName, React.ComponentType<{ size?: number; style?: React.CSSProperties }>> = {
  'shield-check': ShieldCheck,
  'badge-check': BadgeCheck,
  award: Award,
  search: Search,
  'refresh-ccw': RefreshCcw,
  banknote: Banknote,
  clock: Clock,
  wrench: Wrench,
  'file-check': FileCheck,
  headphones: Headphones,
  truck: Truck,
  lock: Lock,
  sparkles: Sparkles,
  handshake: Handshake,
  'thumbs-up': ThumbsUp,
};

interface TrustBadgeItem {
  icon: TrustIconName;
  title: string;
  subtitle?: string;
}

interface TrustBadgesProps {
  sectionTitle?: string;
  subtitle?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  badges?: TrustBadgeItem[];
  columns?: string | number;
  cardStyle?: 'card' | 'minimal';
  iconPosition?: 'top' | 'left';
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
  cardBgColor?: string;
}

const DEFAULT_BADGES: TrustBadgeItem[] = [
  {
    icon: 'shield-check',
    title: 'Garantía 12 meses',
    subtitle: 'Cobertura completa de motor y transmisión',
  },
  {
    icon: 'search',
    title: 'Inspección 150 puntos',
    subtitle: 'Cada vehículo revisado por mecánicos certificados',
  },
  {
    icon: 'refresh-ccw',
    title: 'Devolución 7 días',
    subtitle: 'Si no quedás conforme, te devolvemos tu plata',
  },
  {
    icon: 'banknote',
    title: 'Financiamiento aprobado',
    subtitle: 'En 24 horas, con o sin pie',
  },
];

export const TrustBadges = ({
  sectionTitle = '¿Por qué confiar en nosotros?',
  subtitle = 'Compromisos concretos con cada cliente',
  titleAlignment = 'center',
  badges,
  columns = 4,
  cardStyle = 'card',
  iconPosition = 'top',
  bgColor = '#ffffff',
  textColor = '#111827',
  accentColor = '#e05d31',
  cardBgColor = '#ffffff',
}: TrustBadgesProps) => {
  const { connectors, selected } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const finalBadges = badges && badges.length > 0 ? badges : DEFAULT_BADGES;

  const numColumns = normalizeColumns(columns, 4);
  // Mismos escalones que la grilla anterior (4 columnas arrancan en 2 en móvil),
  // pero como fila flex para que 2 o 3 sellos queden centrados en vez de
  // amontonados a la izquierda con la última columna vacía.
  const itemClass = autoFitItem(
    numColumns === 2
      ? [{ cols: 1 }, { bp: 'sm', cols: 2 }]
      : numColumns === 3
        ? [{ cols: 1 }, { bp: 'sm', cols: 2 }, { bp: 'lg', cols: 3 }]
        : [{ cols: 2 }, { bp: 'lg', cols: 4 }],
  );

  const isCard = cardStyle === 'card';
  const isLeft = iconPosition === 'left';
  const subtitleColor = `${textColor}99`;

  return (
    <div
      ref={(el: HTMLDivElement | null) => {
        if (el) connectors.connect(el);
      }}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        position: 'relative',
        border: selected ? '2px dashed #666' : '1px solid transparent',
        outline: selected ? '1px dashed #999' : 'none',
        outlineOffset: selected ? '2px' : '0px',
      }}
      className='w-full py-16 md:py-20 px-4 sm:px-6 lg:px-8'
    >
      <AreaDePiezas className='max-w-6xl mx-auto'>
        <div className='mb-12' style={{ textAlign: titleAlignment }}>
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            <h2
              className='text-3xl md:text-4xl font-bold mb-3'
              style={{ color: textColor }}
            >
              {sectionTitle}
            </h2>
          </Pieza>
          <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            {subtitle && (
              <p
                className='text-base md:text-lg max-w-2xl'
                style={{
                  color: subtitleColor,
                  marginLeft: titleAlignment === 'center' ? 'auto' : 0,
                  marginRight: titleAlignment === 'center' ? 'auto' : 0,
                }}
              >
                {subtitle}
              </p>
            )}
          </Pieza>
        </div>

        <Pieza clave="insignias" tipo="coleccion" etiqueta="Insignias">
          <div className={`${AUTO_FIT_ROW} gap-6 [--bg-gap:1.5rem]`}>
            {finalBadges.map((badge, index) => {
              const IconComponent = iconMap[badge.icon] || ShieldCheck;
              return (
                <div
                  key={index}
                  className={`${itemClass} ${
                    isCard
                      ? 'p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5'
                      : 'p-2'
                  } ${isLeft ? 'flex items-start gap-4' : 'flex flex-col items-center text-center'}`}
                  style={
                    isCard
                      ? {
                          backgroundColor: cardBgColor,
                          borderColor: `${textColor}14`,
                          boxShadow: `0 1px 3px ${textColor}0d`,
                        }
                      : undefined
                  }
                >
                  <div
                    className={`flex-shrink-0 flex items-center justify-center ${
                      isLeft ? 'w-14 h-14' : 'w-16 h-16 mb-4'
                    }`}
                    style={{
                      backgroundColor: `${accentColor}14`,
                      borderRadius: '50%',
                    }}
                  >
                    <IconComponent
                      size={isLeft ? 26 : 30}
                      style={{ color: accentColor }}
                    />
                  </div>

                  <div className={isLeft ? 'flex-1 min-w-0' : 'w-full'}>
                    <h3
                      className='text-base md:text-lg font-bold leading-tight mb-1.5'
                      style={{ color: textColor }}
                    >
                      {badge.title}
                    </h3>
                    {badge.subtitle !== undefined && badge.subtitle !== '' && (
                      <p
                        className='text-sm leading-relaxed'
                        style={{ color: subtitleColor }}
                      >
                        {badge.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Pieza>
      </AreaDePiezas>
    </div>
  );
};

TrustBadges.craft = {
  displayName: 'TrustBadges',
  props: {
    sectionTitle: '¿Por qué confiar en nosotros?',
    subtitle: 'Compromisos concretos con cada cliente',
    titleAlignment: 'center',
    badges: DEFAULT_BADGES,
    columns: 4,
    cardStyle: 'card',
    iconPosition: 'top',
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
};
